import { LoadingService } from '@ic-monorepo/shared-utils';
import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostBinding,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { distinctUntilChanged, filter } from 'rxjs';
import { ENVIRONMENT } from '@ic-monorepo/shared-injection-tokens';
import { RemoteConfigService } from '@ic-monorepo/data-access-remote-config';

import { PatientAuthFooterComponent } from '@ic-monorepo/shared-auth-patient';
import { FileUploaderPanelComponent } from '@ic-monorepo/shared-components';
import { ShellPageComponent } from '@ic-monorepo/shared-layout';
import {
  AnalyticsEventNames,
  AnalyticsService,
  IntercomService,
  ScreenTrackerService,
  SessionStorageService,
  SystemCheckService,
} from '@ic-monorepo/shared-services';

@Component({
  selector: 'ic-patient-submissions-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    RouterOutlet,
    NgClass,
    ProgressBarModule,
    FileUploaderPanelComponent,
    ToastModule,
    MessageModule,
    ShellPageComponent,
    PatientAuthFooterComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private sessionStorageService = inject(SessionStorageService);
  private screenTrackerService = inject(ScreenTrackerService);
  private systemCheckService = inject(SystemCheckService);
  private analyticsService = inject(AnalyticsService);
  private loadingService = inject(LoadingService);
  private intercomService = inject(IntercomService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private environment = inject(ENVIRONMENT);
  private remoteConfigService = inject(RemoteConfigService);

  @HostBinding('class') hostClasses = 'flex-y h-full';

  enableCookiesMessage =
    'To use our website, you must enable cookies in your browser settings. Cookies are required for many features on our site to work properly. Without cookies, you may experience issues using our site or accessing certain features.';
  isRouteMatched = signal(false);
  isLoadingSignal: Signal<boolean> = toSignal(this.loadingService.loading$);
  isCookiesFeatureEnabled = this.systemCheckService.cookiesEnabled;

  get isAuthOrNewEntryRoute(): boolean {
    return (
      this.router.url.includes('entry/new') ||
      (this.router.url.includes('public/patient') && this.router.url.endsWith('feedback'))
    );
  }

  /**
   * Show the SwUpdate "refresh the page" banner only on the submission landing
   * page, where no data has been entered yet so a reload is safe. Hiding it
   * everywhere else prevents an accidental reload from wiping in-memory form data.
   */
  get hideRefreshBanner(): boolean {
    return !this.router.url.includes('entry/new/identity');
  }

  constructor() {
    this.screenTrackerService.trackScreenView();
  }
  ngOnInit(): void {
    this.initialiseRemoteConfig();
    this.subscribeToRouteChange();
    this.watchAndStoreCurrentUrl();
    this.analyticsService.logEvent(AnalyticsEventNames.PATIENT_APP_VERSION, { APP_VERSION: this.environment.version });
    this.intercomService.initIntercomLauncher();
  }

  /**
   * Fetch and activate Firebase Remote Config after the shell has started.
   * Kept out of APP_INITIALIZER so bootstrap is not blocked; consumers use defaults until ready.
   */
  private initialiseRemoteConfig(): void {
    const fetchInterval = this.environment.production ? 60000 : 0;
    this.remoteConfigService.initialise$(fetchInterval).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  subscribeToRouteChange(): void {
    this.router.events
      .pipe(
        distinctUntilChanged((previous: any, current: any) => {
          return previous instanceof NavigationEnd && current instanceof NavigationEnd && previous.url === current.url;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          // Match against the path only: `routerState.snapshot.url` includes the query
          // string, so a query value like `error=auth-failed` would otherwise false-match.
          const routePath = this.router.routerState.snapshot.url.split('?')[0];
          const routeKeywords = ['auth'];
          this.isRouteMatched.set(routeKeywords.some(keyword => routePath.includes(keyword)));
        }
      });
  }

  watchAndStoreCurrentUrl(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => !event.urlAfterRedirects.includes('/auth/')),
      )
      .subscribe((event: NavigationEnd) => {
        const currentUrl = event.urlAfterRedirects;
        this.sessionStorageService.setItem('previousUrl', currentUrl);
      });
  }
}
